import React, { useContext, useRef } from 'react'
import useIsomorphicLayoutEffect from 'hooks/useIsomorphicLayoutEffect'
import Link from 'components/shared/Link'
import { gsap } from 'gsap'

import useSetStyle from 'hooks/useSetStyle'
import useSpring from 'hooks/useSpring'
import useOnMouseEnter from 'hooks/useOnMouseEnter'
import useRefs from 'hooks/useRefs'
import useTimeline from 'hooks/useTimeline'
import useAnimateInView from 'hooks/useAnimateInView'

import classnames from 'classnames/bind'
import { SeoContext } from 'components/shared/Seo'
import EditorsPicksCard from 'src/components/shared/EditorsPicksCard'
import BlogCloudCTA from 'src/components/shared/BlogCloudCTA'
import css from './styles.module.scss'
import { ReactComponent as EmailIcon } from './icons/email.svg'
import { ReactComponent as LinkedInIcon } from './icons/linkedin.svg'
import { ReactComponent as TwitterIcon } from './icons/twitter.svg'
import { ReactComponent as FacebookIcon } from './icons/facebook.svg'

const cx = classnames.bind(css)

const opacityPipe = gsap.utils.pipe(
  gsap.utils.clamp(0, 1),
  gsap.utils.normalize(0.2, 1)
)

const SocialNetwork = ({ icon, link, forwardRef, name }) => {
  const Icon = icon
  const { href, onClick } = link
  const labelRef = useRef()
  const backgroundRef = useRef()
  const setBackgroundStyle = useSetStyle(backgroundRef)
  const setLabelStyle = useSetStyle(labelRef)
  const [isHover, onMouseEnter, onMouseLeave] = useOnMouseEnter()

  const [setTargetValue] = useSpring({
    progress: 0,
    config: {
      friction: 20,
      rigidity: 0.15,
    },
    onUpdate: ({ progress }, prevValue) => {
      if (prevValue.progress !== progress) {
        setBackgroundStyle({ z: 150 * progress })
        setLabelStyle({
          xPercent: 5 * progress,
          opacity: opacityPipe(progress),
        })
      }
    },
  })

  useIsomorphicLayoutEffect(() => {
    setTargetValue({ progress: isHover ? 1 : 0 })
  }, [isHover])

  return (
    <>
      <li className={css.network}>
        <div ref={forwardRef} className={css.networkContainer}>
          <div ref={backgroundRef} className={css.background} />
          <Link
            external
            className={css.networkLink}
            href={href}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onClick={() => {
              onClick && onClick(href)
            }}
          >
            <Icon className={css.networkIcon} />
          </Link>
        </div>
      </li>
    </>
  )
}

const Sharer = ({ className, layout }) => {
  const { title, description, url } = useContext(SeoContext)

  const networks = [
    {
      name: 'facebook',
      icon: FacebookIcon,
      link: {
        href: encodeURI(`http://www.facebook.com/sharer.php?u=${url}`),
        onClick: (href) => {
          window.open(
            href,
            'facebookwindow',
            'left=20,top=20,width=600,height=700,toolbar=0,resizable=1'
          )

          return false
        },
      },
    },
    {
      name: 'linkedin',
      icon: LinkedInIcon,
      link: {
        href: encodeURI(
          `http://www.linkedin.com/shareArticle?url=${url}&title=${title}`
        ),
        onClick: (href) => {
          window.open(
            href,
            'linkedinwindow',
            'left=20,top=20,width=600,height=700,toolbar=0,resizable=1'
          )

          return false
        },
      },
    },
    {
      name: 'twitter',
      icon: TwitterIcon,
      link: {
        href: encodeURI(
          `http://twitter.com/intent/tweet?url=${url}&text=${title}`
        ),
        onClick: (href) => {
          window.open(
            href,
            'twitterwindow',
            'left=20,top=20,width=600,height=300,toolbar=0,resizable=1'
          )

          return false
        },
      },
    },
    {
      name: 'email',
      icon: EmailIcon,
      link: {
        title: 'Share by email',
        href: encodeURI(
          `mailto:?subject=${title}&body=${`${description} ${url}`}`
        ),
      },
    },
  ]

  const refs = useRefs(networks.length + 1)

  const animateIn = useTimeline({ paused: true, delay: 0.3 }, (tl) => {
    const stagger = 0.12
    const networksRef = refs.current.map((ref) => ref.current)
    tl.fromTo(
      networksRef,
      {
        opacity: 0,
      },
      {
        opacity: 1,
        duration: 0.2,
        stagger,
        ease: 'none',
      }
    )
    tl.fromTo(
      networksRef,
      {
        scale: 0,
      },
      {
        scale: 1,
        transformOrigin: 'center',
        ease: 'elastic.out(0.4, 0.25)',
        duration: 0.6,
        stagger,
      },
      '<'
    )
  })

  const [inViewRef] = useAnimateInView({
    threshold: 0,
    triggerOnce: true,
    animateIn: () => animateIn.restart(true),
  })
  return (
    <ul ref={inViewRef} className={cx(className, css.sharer, layout)}>
      {networks.map(({ name, ...network }, index) => {
        return (
          <SocialNetwork
            forwardRef={refs.current[index]}
            key={name}
            name={name}
            {...network}
          />
        )
      })}
    </ul>
  )
}

Sharer.defaultProps = {}

export default Sharer
